import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Shield,
  Users,
  User,
  BarChart3,
  DollarSign,
  Bell,
  X,
  Edit3,
  MessageCircle,
  Sparkles,
  LayoutDashboard,
  Upload,
  Trash2,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';
import { SupportBubble } from '@/components/support/SupportBubble';
import { useEditMode } from '@/contexts/EditModeContext';
import { HStack, VStack } from '@/components/core';
import { apiClient } from '@/lib/api/client';
import { clearDraftOverridesCache } from '@/lib/i18n-draft-cache';
import { DRAFT_CHANGED_EVENT } from '@/components/i18n/GlobalTextEditor';

export function AdminFloatingButton() {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSupportBubble, setShowSupportBubble] = useState(false);
  const { isEditMode, setEditMode, isGuestView, setGuestView } = useEditMode();
  const [draftCount, setDraftCount] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const fetchDraftCount = useCallback(async () => {
    try {
      const res = await apiClient.get<{ count: number }>('/admin/i18n/drafts/count');
      setDraftCount(res.data?.count || 0);
    } catch {
      setDraftCount(0);
    }
  }, []);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'root_admin' || user.role === 'demo_admin')) {
      void fetchDraftCount();

      const handleDraftChanged = () => {
        void fetchDraftCount();
      };
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          void fetchDraftCount();
        }
      };
      window.addEventListener(DRAFT_CHANGED_EVENT, handleDraftChanged);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        window.removeEventListener(DRAFT_CHANGED_EVENT, handleDraftChanged);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user, fetchDraftCount]);

  const handlePublishAll = async () => {
    if (draftCount === 0 || isPublishing) return;
    setIsPublishing(true);
    try {
      await apiClient.post('/admin/i18n/publish-all', null, { params: { trigger_build: false } });
      // Clear LocalStorage cache so user sees real published state
      clearDraftOverridesCache();
      // Reload to fetch fresh translations from server
      window.location.reload();
    } catch (e) {
      console.error('Publish failed', e);
      setIsPublishing(false);
    }
  };

  const handleDiscardAll = async () => {
    if (draftCount === 0 || isDiscarding) return;
    if (!confirm(t('floatingButton.discardConfirm'))) return;
    setIsDiscarding(true);
    try {
      await apiClient.post('/admin/i18n/discard-all');
      // Clear LocalStorage cache so user sees original state
      clearDraftOverridesCache();
      // Reload to restore original translations
      window.location.reload();
    } catch (e) {
      console.error('Discard failed', e);
      setIsDiscarding(false);
    }
  };

  // Show only for Super-Admins and Super-Super-Admins
  if (!user || (user.role !== 'admin' && user.role !== 'root_admin' && user.role !== 'demo_admin')) {
    return null;
  }

  // Important routes (highlighted)
  const importantLinks = [
    { to: '/admin/dashboard', labelKey: 'floatingButton.links.dashboard', icon: LayoutDashboard, highlight: false },
    { to: '/admin/moderation', labelKey: 'floatingButton.links.moderation', icon: Shield, highlight: true },
    { to: '/admin/characters', labelKey: 'floatingButton.links.characters', icon: User, highlight: false },
    { to: '/admin/users', labelKey: 'floatingButton.links.users', icon: Users, highlight: false },
  ] as const;

  // Standard routes
  const adminLinks = [
    { to: '/admin/analytics', labelKey: 'floatingButton.links.analytics', icon: BarChart3 },
    { to: '/admin/billing', labelKey: 'floatingButton.links.billing', icon: DollarSign },
    { to: '/admin/notification-settings', labelKey: 'floatingButton.links.notifications', icon: Bell },
  ] as const;

  return (
    <>
      {/* Backdrop */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Admin Menu */}
      {isMenuOpen && (
        <div className="fixed right-4 top-20 md:top-auto md:bottom-20 design-card shadow-token-modal z-50 min-w-[280px] max-w-[320px]">
          <div className="p-token-xs">
            {/* Header */}
            <div className="px-token-sm py-token-sm mb-token-xs">
              <HStack gap="sm" align="center">
                <div className="p-1.5 rounded-token-sm bg-info/20">
                  <Shield className="h-4 w-4 text-info" />
                </div>
                <span className="design-text-primary font-semibold text-sm">
                  {t('floatingButton.title')}
                </span>
              </HStack>
            </div>

            {/* Text Editor Toggle */}
            <div className="mb-token-sm px-token-sm">
              <button
                type="button"
                onClick={() => setEditMode(!isEditMode)}
                className={`flex items-center justify-between gap-token-sm w-full p-token-sm rounded-token-button text-sm transition-all ${
                  isEditMode
                    ? 'bg-success/20 border border-success/30 hover:bg-success/30'
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
              >
                <span className="flex items-center gap-token-sm">
                  <Edit3 className={`h-4 w-4 ${isEditMode ? 'text-success' : 'design-text-secondary'}`} />
                  <span className={isEditMode ? 'text-success font-medium' : 'design-text-primary'}>
                    {t('floatingButton.textEditor')}
                  </span>
                </span>
                <span
                  className={`inline-flex items-center gap-token-xs px-token-sm py-0.5 rounded-full text-xs font-medium ${
                    isEditMode
                      ? 'bg-success/30 text-success'
                      : 'bg-muted/20 text-muted'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${isEditMode ? 'bg-success' : 'bg-muted'}`} />
                  {isEditMode ? t('floatingButton.on') : t('floatingButton.off')}
                </span>
              </button>

              {/* Guest View Toggle - Admin can view site as guest */}
              <button
                type="button"
                onClick={() => setGuestView(!isGuestView)}
                className={`flex items-center justify-between gap-token-sm w-full p-token-sm rounded-token-button text-sm transition-all mt-token-xs ${
                  isGuestView
                    ? 'bg-warning/20 border border-warning/30 hover:bg-warning/30'
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
              >
                <span className="flex items-center gap-token-sm">
                  {isGuestView ? (
                    <EyeOff className="h-4 w-4 text-warning" />
                  ) : (
                    <Eye className="h-4 w-4 design-text-secondary" />
                  )}
                  <span className={isGuestView ? 'text-warning font-medium' : 'design-text-primary'}>
                    {t('floatingButton.guestView')}
                  </span>
                </span>
                <span
                  className={`inline-flex items-center gap-token-xs px-token-sm py-0.5 rounded-full text-xs font-medium ${
                    isGuestView
                      ? 'bg-warning/30 text-warning'
                      : 'bg-muted/20 text-muted'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${isGuestView ? 'bg-warning' : 'bg-muted'}`} />
                  {isGuestView ? t('floatingButton.on') : t('floatingButton.off')}
                </span>
              </button>

              {/* Publish/Discard Buttons - only visible when drafts exist */}
              {draftCount > 0 && (
                <VStack gap="xs" className="mt-token-sm">
                  <div className="text-xs text-warning text-center mb-token-xs">
                    {t('floatingButton.unpublishedChanges', { count: draftCount })}
                  </div>
                  <HStack gap="xs">
                    <button
                      type="button"
                      onClick={handlePublishAll}
                      disabled={isPublishing || isDiscarding}
                      className="flex-1 flex items-center justify-center gap-token-xs p-token-sm rounded-token-button text-xs font-medium bg-success/20 border border-success/30 hover:bg-success/30 text-success disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isPublishing ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Upload className="h-3 w-3" />
                      )}
                      {t('floatingButton.publish')}
                    </button>
                    <button
                      type="button"
                      onClick={handleDiscardAll}
                      disabled={isPublishing || isDiscarding}
                      className="flex-1 flex items-center justify-center gap-token-xs p-token-sm rounded-token-button text-xs font-medium bg-error/20 border border-error/30 hover:bg-error/30 text-error disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isDiscarding ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                      {t('floatingButton.discard')}
                    </button>
                  </HStack>
                </VStack>
              )}
            </div>

            <div className="h-px bg-white/10 my-token-sm" />

            {/* Important Routes */}
            <div className="px-token-sm mb-token-sm">
              <HStack gap="sm" align="center" className="px-token-sm py-token-xs mb-token-xs">
                <Sparkles className="h-3 w-3 text-warning" />
                <span className="text-xs font-medium text-warning">{t('floatingButton.importantRoutes')}</span>
              </HStack>
              {importantLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-token-sm w-full p-token-sm rounded-token-button text-sm transition-all mb-token-xs ${
                      link.highlight
                        ? 'bg-warning/10 border border-warning/20 hover:bg-warning/20 design-text-primary font-medium'
                        : 'hover:bg-white/10 design-text-secondary hover:design-text-primary'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${link.highlight ? 'text-warning' : ''}`} />
                    {t(link.labelKey)}
                  </Link>
                );
              })}
            </div>

            <div className="h-px bg-white/10 my-token-sm" />

            {/* Standard Routes */}
            <div className="px-token-sm mb-token-sm">
              <div className="px-token-sm py-token-xs mb-token-xs">
                <span className="text-xs font-medium design-text-secondary">{t('floatingButton.moreLinks')}</span>
              </div>
              {adminLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-token-sm w-full p-token-sm rounded-token-button text-sm hover:bg-white/10 design-text-secondary hover:design-text-primary transition-all mb-token-xs"
                  >
                    <Icon className="h-4 w-4" />
                    {t(link.labelKey)}
                  </Link>
                );
              })}
            </div>

            <div className="h-px bg-white/10 my-token-sm" />

            {/* Support Bubble Toggle */}
            <div className="px-token-sm pb-token-xs">
              <button
                type="button"
                onClick={() => setShowSupportBubble((v) => !v)}
                className="flex items-center justify-between gap-token-sm w-full p-token-sm rounded-token-button text-sm hover:bg-white/10 design-text-secondary hover:design-text-primary transition-all"
              >
                <span className="flex items-center gap-token-sm">
                  <MessageCircle className="h-4 w-4" />
                  {t('floatingButton.supportBubble')}
                </span>
                <span className={`text-xs font-medium ${showSupportBubble ? 'text-success' : 'design-text-secondary'}`}>
                  {showSupportBubble ? t('floatingButton.on') : t('floatingButton.off')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <div className="fixed right-4 top-4 md:top-auto md:bottom-4 z-40">
        <Button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={`h-12 w-12 rounded-full shadow-token-card transition-all duration-200 ${
            isMenuOpen
              ? 'bg-error hover:bg-error/90 rotate-180'
              : 'bg-info hover:bg-info/90 hover:scale-110'
          }`}
          size="icon"
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
        </Button>
      </div>

      {/* Render Support Bubble when toggled */}
      {showSupportBubble && <SupportBubble hideForAdmins={false} />}
    </>
  );
}
